// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { ErrorCode, ErrorMessage } from '@errorHandler/constants';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { Button, Modal } from '@imprivata-cloud/components';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  mapErrorCodeToStatus,
  OTP_CHALLENGE_STATUS,
  OtpChallenge,
  type OtpChallengeCopy,
  type OtpChallengeStatus,
} from './OtpChallenge';
import './ConfirmOwnershipModal.less';

const MAX_OTP_ATTEMPTS = 5;

export type ChallengeEffects = {
  request: () => Promise<{ otpId: string }>;
  confirm: (otpId: string, code: string) => Promise<unknown>;
};

interface ConfirmOwnershipModalProps {
  open: boolean;
  email: string | undefined;
  otpId: string | null;
  codeIssuedAtMs: number | null;
  effects: ChallengeEffects;
  translationRoot: string;
  onResent: (otpId: string, issuedAtMs: number) => void;
  onVerified: () => void;
  onCancel: () => void;
  getContainer?: () => HTMLElement;
}

const ConfirmOwnershipModal = ({
  open,
  email,
  otpId,
  codeIssuedAtMs,
  effects,
  translationRoot,
  onResent,
  onVerified,
  onCancel,
  getContainer,
}: ConfirmOwnershipModalProps) => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const [status, setStatus] = useState<OtpChallengeStatus | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptsUsed, setAttemptsUsed] = useState(0);

  const COPY: OtpChallengeCopy = {
    [OTP_CHALLENGE_STATUS.SENT]: `${translationRoot}.status.sent`,
    [OTP_CHALLENGE_STATUS.INCORRECT]: `${translationRoot}.status.incorrect`,
    [OTP_CHALLENGE_STATUS.INVALIDATED]: `${translationRoot}.status.invalidated`,
    [OTP_CHALLENGE_STATUS.EXPIRED]: `${translationRoot}.status.expired`,
    resendLink: `${translationRoot}.resend-link`,
    resendPrompt: `${translationRoot}.resend-prompt`,
    countdown: `${translationRoot}.countdown`,
    attemptsRemaining: `${translationRoot}.attempts-remaining`,
  };

  const handleSubmit = useCallback(
    async (code: string) => {
      if (!otpId) {
        emitError(AppError.fromMessage(ErrorMessage.SYSTEM_ERROR, ErrorCode.UNKNOWN));
        return;
      }
      setIsSubmitting(true);
      try {
        await effects.confirm(otpId, code);
        onVerified();
      } catch (error) {
        const nextStatus = mapErrorCodeToStatus(error);
        if (nextStatus === null) {
          emitError(new AppError(error as ApiError));
        } else if (nextStatus === OTP_CHALLENGE_STATUS.INCORRECT) {
          const used = attemptsUsed + 1;
          setAttemptsUsed(used);
          setStatus(used >= MAX_OTP_ATTEMPTS ? OTP_CHALLENGE_STATUS.INVALIDATED : nextStatus);
        } else {
          setStatus(nextStatus);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [otpId, onVerified, emitError, attemptsUsed, effects],
  );

  const handleResend = useCallback(async () => {
    try {
      const response = await effects.request();
      onResent(response.otpId, Date.now());
      setStatus(OTP_CHALLENGE_STATUS.SENT);
      setAttemptsUsed(0);
    } catch (error) {
      emitError(new AppError(error as ApiError));
    }
  }, [onResent, emitError, effects]);

  const isVerifyBlocked =
    attemptsUsed >= MAX_OTP_ATTEMPTS || status === OTP_CHALLENGE_STATUS.INVALIDATED;

  const clearStatus = useCallback(() => setStatus(null), []);

  return (
    <Modal
      open={open}
      destroyOnHidden
      maskClosable={false}
      centered
      title={t(`${translationRoot}.modal-title`)}
      footer={null}
      onCancel={onCancel}
      getContainer={getContainer}
      className="confirm-ownership-modal"
      wrapClassName="confirm-ownership-modal-wrap"
      width={432}
    >
      <OtpChallenge
        heading={
          <>
            <p>{t(`${translationRoot}.heading`)}</p>
            {email && (
              <p>
                <strong>{email}</strong>
              </p>
            )}
          </>
        }
        groupLabel={t(`${translationRoot}.heading`)}
        copy={COPY}
        statusVariant="inline"
        codeIssuedAtMs={codeIssuedAtMs}
        status={status}
        attemptsRemaining={MAX_OTP_ATTEMPTS - attemptsUsed}
        onStatusClear={clearStatus}
        onSubmit={handleSubmit}
        onResend={handleResend}
        isSubmitting={isSubmitting}
        actions={({ canSubmit, submit }) => (
          <>
            <Button label={t('common.cancel')} onClick={onCancel} />
            <Button
              type="primary"
              label={t(`${translationRoot}.verify-button`)}
              disabled={!canSubmit || isVerifyBlocked}
              onClick={submit}
            />
          </>
        )}
      />
    </Modal>
  );
};

export default ConfirmOwnershipModal;
